/**
 * Менеджер пагинации результатов поиска
 * Обрабатывает навигацию, обновление URL и динамическую загрузку
 */
class SearchPagination {
    constructor(config = {}) {
        this.config = {
            cardsContainer: '.results__cards',
            paginationContainer: '.results__pagination',
            placesContainer: '.results__places',
            loadMoreSelector: '.results__load-more',
            apiEndpoint: config.apiEndpoint,
            apiPopulars: config.apiPopulars,
            resultsPerPage: config.resultsPerPage || 10,
            maxPaginationLinks: config.maxPaginationLinks || 5,
            retryAttempts: config.retryAttempts || 3,
            retryDelay: config.retryDelay || 1000,
            skeletonDelay: config.skeletonDelay || 150,
            autoLoadOnMobile: config.autoLoadOnMobile ?? true,  // Автозагрузка при скролле (мобильные)
            autoLoadThreshold: config.autoLoadThreshold || 0.5, // 50% видимости кнопки
            mobileMaxWidth: config.mobileMaxWidth || 768,
            debug: config.debug ?? false,
            // SEO meta шаблоны
            siteName: config.siteName || 'ПоВятке',
            baseUrl: config.baseUrl || window.location.origin,
            titleHome: config.titleHome || '{siteName} - региональный поисковик',
            titleSearch: config.titleSearch || '{siteName} - региональный поиск: {query}{pageInfo}',
            descHome: config.descHome || 'Региональный поисковик {siteName}.',
            descSearch: config.descSearch || 'Региональный поисковик {siteName}. Результаты поиска {query}{descPageInfo}',
            ...config,
            messages: {
                noResultsTitle: 'По запросу "{query}" ничего не найдено.',
                noResultsHint: 'Попробуйте изменить запрос или использовать другие ключевые слова.',
                popularQueriesTitle: 'Популярные запросы сегодня:',
                errorTitle: 'Произошла ошибка при загрузке результатов.',
                errorHint: 'Пожалуйста, попробуйте позже.',
                loadMore: 'Загрузить ещё',
                loading: 'Идёт загрузка...',
                placesTitle: 'Места',
                ...(config.messages || {})
            }
        };

        this.elements = {
            cardsContainer: document.querySelector(this.config.cardsContainer),
            paginationContainer: document.querySelector(this.config.paginationContainer),
            placesContainer: document.querySelector(this.config.placesContainer),
            companyContainer: document.querySelector('[data-results-company]'),
            searchInput: document.querySelector('[data-searcher-input]'),
            searchButton: document.querySelector('[data-searcher-button]')
        };

        if (!this.elements.cardsContainer) {
            if (this.config.debug) {
                console.warn('[SearchPagination] cards container not found');
            }
            return;
        }

        this.currentPage = 1;
        this.totalPages = 1;
        this.currentQuery = '';
        /** Запрос для подсветки в сниппетах (после автоисправления раскладки может отличаться от URL) */
        this.queryForHighlight = '';
        this.isLoading = false;
        this.skeletonTimer = null;
        this.loadMoreObserver = null;  // IntersectionObserver для автозагрузки
        this.scrollTopButton = null;   // Кнопка "Вверх"
        this.isMobile = this.detectMobile();

        // Callback hooks (can be overridden)
        this.afterLoadResults = null;

        // Bound handlers for cleanup
        this.boundHandlers = {
            popstate: this.onPopState.bind(this),
            paginationClick: this.onPaginationClick.bind(this),
            searchSubmit: this.onSearchSubmit.bind(this),
            scroll: null  // Для кнопки "Вверх"
        };

        this.init();
    }

    /**
     * Initialize pagination
     */
    init() {
        window._searchPagination = this;

        // Get initial state from URL
        const urlParams = this.getUrlParams();
        this.currentQuery = urlParams.q || '';
        this.currentPage = parseInt(urlParams.page) || 1;

        // Set search input value from URL
        if (this.elements.searchInput) {
            this.elements.searchInput.value = this.currentQuery;
        }

        // Set initial history state
        if (!window.history.state) {
            window.history.replaceState(
                { query: this.currentQuery, page: this.currentPage },
                '',
                window.location.href
            );
        }

        // Setup event listeners
        window.addEventListener('popstate', this.boundHandlers.popstate);

        if (this.elements.paginationContainer) {
            this.elements.paginationContainer.addEventListener('click', this.boundHandlers.paginationClick);
        }

        // Обработчик click на searchButton в main.js

        // Setup "Load More" button for mobile
        const loadMoreBtn = document.querySelector('.results__more-button');
        if (loadMoreBtn) {
            this.boundHandlers.loadMore = this.onLoadMoreClick.bind(this);
            loadMoreBtn.addEventListener('click', this.boundHandlers.loadMore);
        }

        // Автозагрузка при скролле (мобильные)
        if (this.config.autoLoadOnMobile && this.isMobile) {
            this.initAutoLoad();
        }

        // Кнопка "Вверх" для мобильных
        if (this.isMobile) {
            this.createScrollTopButton();
        }
    }

    /**
     * Get URL parameters
     */
    getUrlParams() {
        const params = new URLSearchParams(window.location.search);
        return {
            q: params.get('q'),
            page: params.get('page')
        };
    }

    /**
     * Update URL without reload
     */
    updateUrl(query, page, pushState = true) {
        // Сохраняем предыдущий URL для referer
        const prevUrl = window.location.href;

        const params = new URLSearchParams();
        if (query) params.set('q', query);
        if (page > 1) params.set('page', page);

        const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;

        if (pushState) {
            window.history.pushState({ query, page }, '', newUrl);
        } else {
            window.history.replaceState({ query, page }, '', newUrl);
        }

        // Обновляем title и description
        this.updatePageMeta(query, page, this.totalPages);

        // Отправляем hit в Яндекс.Метрику (после обновления title)
        this.trackPageView(newUrl, prevUrl);
    }

    /**
     * Отправить просмотр страницы в Яндекс.Метрикe
     */
    trackPageView(url, referer) {
        if (typeof ym !== 'undefined') {
            const counterKey = Object.keys(window).find(k => k.startsWith('yaCounter'));
            if (counterKey) {
                const counterId = parseInt(counterKey.replace('yaCounter', ''));
                if (counterId) {
                    ym(counterId, 'hit', url, {
                        title: document.title,
                        referer: referer || ''
                    });
                }
            }
        }
    }

    /**
     * Обновить title и meta description страницы
     */
    updatePageMeta(query, page, totalPages) {
        const { siteName, titleHome, titleSearch, descHome, descSearch } = this.config;

        const baseTitle = titleHome.replace('{siteName}', siteName);
        const baseDescription = descHome.replace('{siteName}', siteName);

        if (!query) {
            // Главная страница без поиска
            document.title = baseTitle;
            this.setMetaDescription(baseDescription);
            this.updateH2('');
            return;
        }

        // Обрезаем запрос если слишком длинный (для title)
        const maxQueryLength = 50;
        const truncatedQuery = query.length > maxQueryLength
            ? query.substring(0, maxQueryLength) + '...'
            : query;

        // Формат title из шаблона
        const pageInfo = totalPages > 1 ? ` [${page}/${totalPages}]` : '';
        document.title = titleSearch
            .replace('{siteName}', siteName)
            .replace('{query}', truncatedQuery)
            .replace('{pageInfo}', pageInfo);

        // Meta description из шаблона
        const descPageInfo = totalPages > 1 ? ` страница ${page} из ${totalPages}` : '';
        const description = descSearch
            .replace('{siteName}', siteName)
            .replace('{query}', query)
            .replace('{descPageInfo}', descPageInfo);
        this.setMetaDescription(description);
        this.updateH2(query, page);
    }

    /**
     * Обновить или создать h2 с результатами поиска
     */
    updateH2(query, page = 1) {
        let h2 = document.querySelector('h2.sr-only');

        if (!query) {
            // Удаляем h2 если нет запроса
            if (h2) h2.remove();
            return;
        }

        const pageInfo = page > 1 ? ` [страница ${page}]` : '';
        const text = `Результаты поиска: ${query}${pageInfo}`;

        if (h2) {
            h2.textContent = text;
        } else {
            // Создаём h2 после h1
            const h1 = document.querySelector('h1.sr-only');
            if (h1) {
                h2 = document.createElement('h2');
                h2.className = 'sr-only';
                h2.textContent = text;
                h1.after(h2);
            }
        }
    }

    /**
     * Установить meta description
     */
    setMetaDescription(content) {
        let meta = document.querySelector('meta[name="description"]');
        if (!meta) {
            meta = document.createElement('meta');
            meta.name = 'description';
            document.head.appendChild(meta);
        }
        meta.content = content;
    }

    /**
     * Handle browser back/forward buttons
     */
    onPopState(event) {
        if (event.state) {
            this.currentQuery = event.state.query || '';
            this.currentPage = event.state.page || 1;

            // Update search input value (bug fix: back button)
            if (this.elements.searchInput) {
                this.elements.searchInput.value = this.currentQuery;

                // Update clear button state
                const clearButton = document.querySelector('[data-searcher-clear]');
                if (clearButton) {
                    if (this.currentQuery) {
                        clearButton.classList.add('_active');
                    } else {
                        clearButton.classList.remove('_active');
                    }
                }
            }

            if (this.currentQuery) {
                this.loadResults(this.currentQuery, this.currentPage, false);
                // Show results mode
                if (window.showSearchResults) {
                    window.showSearchResults();
                }
            } else {
                // Clear results when going back to empty search
                if (this.elements.cardsContainer) {
                    this.elements.cardsContainer.innerHTML = '';
                }
                if (this.elements.paginationContainer) {
                    this.elements.paginationContainer.innerHTML = '';
                }
                this.hideLoadMoreButton();

                // Сбрасываем title и description
                this.updatePageMeta('', 1, 1);

                // Switch to search mode
                if (window.showSearchMode) {
                    window.showSearchMode();
                }

                // Remove button states
                if (this.elements.searchButton) {
                    this.elements.searchButton.classList.remove('_searched');
                }
            }
        }
    }

    /**
     * Handle pagination link clicks
     */
    onPaginationClick(e) {
        const target = e.target.closest('a.pagination__page-link, a.pagination__page-next, a.pagination__page-prev');
        if (!target) return;

        e.preventDefault();

        const href = target.getAttribute('href');
        const params = new URLSearchParams(href.split('?')[1] || '');
        const page = parseInt(params.get('page')) || 1;

        this.goToPage(page);
    }

    /**
     * Handle search button click
     */
    onSearchSubmit(e) {
        e.preventDefault();

        if (!this.elements.searchInput) return;

        const query = this.elements.searchInput.value.trim();
        if (!query) return;

        // Переключаем в режим результатов
        if (typeof window.showSearchResults === 'function') {
            window.showSearchResults();
        }

        this.currentQuery = query;
        this.currentPage = 1;
        this.loadResults(query, 1, true);
    }

    /**
     * Handle "Load More" button click
     */
    onLoadMoreClick(e) {
        e.preventDefault();

        if (this.currentPage >= this.totalPages) return;
        if (this.isLoading) return;

        this.currentPage++;
        this.appendResults(this.currentQuery, this.currentPage);
    }

    /**
     * Append results instead of replacing (for mobile)
     */
    async appendResults(query, page) {
        if (this.isLoading) return;
        if (!query) return;

        this.isLoading = true;
        const loadMoreBtn = document.querySelector('.results__more-button, .results__load-more button');
        const loadMoreContainer = document.querySelector('.results__load-more');

        if (loadMoreBtn) {
            loadMoreBtn.classList.add('_loading');
            loadMoreBtn.textContent = this.config.messages.loading;
        }
        if (loadMoreContainer) {
            loadMoreContainer.classList.add('_loading');
        }

        try {
            const params = new URLSearchParams({
                q: query,
                page: page,
                limit: this.config.resultsPerPage
            });

            const response = await this.fetchWithRetry(`${this.config.apiEndpoint}?${params.toString()}`);
            const data = await response.json();

            if (data.query) {
                this.queryForHighlight = String(data.query);
            }
            const hq = this.queryForHighlight || query;

            if (data.results && Array.isArray(data.results) && data.results.length > 0) {
                const startIndex = (page - 1) * this.config.resultsPerPage;

                const newCards = data.results.map((result, index) => {
                    const num = startIndex + index + 1;
                    const faviconClass = this.getFaviconClass(num);
                    const domain = result.domain || this.extractDomain(result.url);
                    return `
                    <div class="results__card" data-uid="${this.escapeHtml(result.id || '')}">
                        <div class="${faviconClass}">
                            ${num}.
                        </div>
                        <h3 class="results__card-title">
                            <a href="${this.escapeHtml(result.url)}" target="_blank" rel="noopener">${this.highlightQuery(result.title, hq)}</a>
                        </h3>
                        <div class="results__card-domain">
                            <img class="results__card-domain-favicon" src="https://${this.escapeHtml(domain)}/favicon.ico" width="18" height="18" alt="" loading="lazy" onerror="this.style.display='none'">${this.escapeHtml(domain)}
                        </div>
                        <div class="results__card-snippet">
                            ${this.highlightQuery(result.snippet, hq)}
                        </div>
                        <span class="icon_menu results__card-menu-button" data-card-menu-trigger="${result.id}"></span>
                    </div>
                `}).join('');

                // Append to existing cards
                if (this.elements.cardsContainer) {
                    this.elements.cardsContainer.insertAdjacentHTML('beforeend', newCards);
                }

                this.currentPage = page;
                this.totalPages = data.totalPages || 1;
                this.updateUrl(query, page, true);

                // Update SearchLinker cards data
                if (window.searchLinkerHandle) {
                    window.searchLinkerHandle.updateCardsList();
                }

                // Update "Load More" button visibility
                this.updateLoadMoreButton(page, this.totalPages);

                // Показываем кнопку "Вверх" после подгрузки (мобильные)
                if (this.isMobile && page > 1) {
                    this.showScrollTopButton();
                }
            }

        } catch (err) {
            this.error('Ошибка добавления результатов:', err);
        } finally {
            this.isLoading = false;
            if (loadMoreBtn) {
                loadMoreBtn.classList.remove('_loading');
                loadMoreBtn.textContent = this.config.messages.loadMore;
            }
            if (loadMoreContainer) {
                loadMoreContainer.classList.remove('_loading');
            }
        }
    }

    /**
     * Load more results (wrapper for appendResults)
     */
    async loadMoreResults(query, page) {
        return this.appendResults(query, page);
    }

    /**
     * Navigate to specific page
     */
    goToPage(page) {
        if (page < 1 || page > this.totalPages || page === this.currentPage) return;
        if (this.isLoading) return;

        this.currentPage = page;
        this.loadResults(this.currentQuery, page, true);

        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    /**
     * Load search results from API
     */
    async loadResults(query, page = 1, pushState = true) {
        if (this.isLoading) return;
        if (!query) return;

        this.isLoading = true;
        this.currentQuery = query;
        this.currentPage = page;
        this.showLoading();

        // Скрываем кнопку "Вверх" при новом поиске
        if (page === 1) {
            this.hideScrollTopButton();
            this.hidePlaces();
        }

        try {
            const params = new URLSearchParams({
                q: query,
                page: page,
                limit: this.config.resultsPerPage
            });

            const response = await this.fetchWithRetry(`${this.config.apiEndpoint}?${params.toString()}`);
            const data = await response.json();

            this.queryForHighlight = (data.query && String(data.query)) || query;

            if (data.results && Array.isArray(data.results) && data.results.length > 0) {
                // Рендерим places только на первой странице
                if (page === 1 && data.places && data.places.length > 0) {
                    this.renderPlaces(data.places);
                } else {
                    this.hidePlaces();
                }

                if (page === 1 && data.company) {
                    this.renderCompany(data.company);
                } else if (page === 1) {
                    this.hideCompany();
                }

                this.renderResults(data.results);
                if (page === 1) {
                    this.renderTickets(data.tickets || []);
                }
                this.totalPages = data.totalPages || 1;
                this.renderPagination(page, this.totalPages);
                this.renderFooter();
                if (page === 1) {
                    this.renderQueryCorrectionNotice(data);
                }

                // Обновляем URL и мета-теги после получения totalPages
                this.updateUrl(query, page, pushState);

                // Update "Load More" button visibility
                this.updateLoadMoreButton(page, this.totalPages);

                // Update SearchLinker cards data
                if (window.searchLinkerHandle) {
                    window.searchLinkerHandle.updateCardsList();
                }

                // Снимаем фокус с input (закрывает клавиатуру на мобильных)
                if (this.elements.searchInput) {
                    this.elements.searchInput.blur();
                }

                // Call callback hook
                if (typeof this.afterLoadResults === 'function') {
                    this.afterLoadResults(true, data);
                }
            } else {
                this.showNoResults();
                this.totalPages = 0;
                if (page === 1) {
                    this.renderQueryCorrectionNotice(data);
                }

                // Обновляем URL и мета-теги
                this.updateUrl(query, page, pushState);

                // Hide "Load More" button when no results
                this.hideLoadMoreButton();

                // Call callback hook
                if (typeof this.afterLoadResults === 'function') {
                    this.afterLoadResults(false, data);
                }
            }

        } catch (err) {
            this.error('Ошибка загрузки результатов поиска:', err);
            this.showError();
            this.hideLoadMoreButton();
        } finally {
            this.isLoading = false;
            this.hideLoading();

            // Remove loading state from searcher
            if (window.searcherHandle && typeof window.searcherHandle.removeLoadingState === 'function') {
                window.searcherHandle.removeLoadingState();
            }
        }
    }

    /**
     * Render search result cards
     */
    renderTickets(tickets) {
        if (!this.elements.cardsContainer) return;

        // Удаляем старые билеты
        this.elements.cardsContainer.querySelector('.results__tickets')?.remove();

        if (!tickets || tickets.length === 0) return;

        const ticketIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24"><path fill="#f4e400" stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2 9a3 3 0 1 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z"/><path stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5v2M13 17v2M13 11v2"/></svg>`;

        const html = tickets.map(ticket => `
            <div class="results__ticket">
                <div class="results__ticket-icon">${ticketIcon}</div>
                <a href="${this.escapeHtml(ticket.url)}" class="results__ticket-link" target="_blank" rel="noopener">
                    ${this.escapeHtml(ticket.text)} &#8594;
                </a>
            </div>
        `).join('');

        this.elements.cardsContainer.insertAdjacentHTML('afterbegin', `<div class="results__tickets">${html}</div>`);
    }

    /**
     * Баннеры п. 2.5: автоисправление раскладки, «возможно имели в виду», латиница.
     */
    renderQueryCorrectionNotice(data) {
        const el = document.querySelector('[data-results-correction]');
        if (!el) {
            return;
        }
        const qc = data && data.query_correction;
        if (!qc || this.currentPage !== 1) {
            el.innerHTML = '';
            el.classList.remove('results__correction_visible');
            return;
        }
        const base = this.config.baseUrl || window.location.origin;
        const parts = [];
        if (qc.keyboard_auto && qc.keyboard_auto_typed) {
            const qe = this.escapeHtml(data.query || '');
            const qt = this.escapeHtml(qc.keyboard_auto_typed);
            parts.push(`<p class="results__correction-line">Показаны результаты по запросу «${qe}». <a href="${this.escapeHtml(base)}/?q=${encodeURIComponent(qc.keyboard_auto_typed)}">Искать именно: ${qt}</a></p>`);
        }
        if (qc.mixed_auto && qc.mixed_auto_typed) {
            const qe = this.escapeHtml(data.query || '');
            const qt = this.escapeHtml(qc.mixed_auto_typed);
            parts.push(`<p class="results__correction-line">Показаны результаты по запросу «${qe}». <a href="${this.escapeHtml(base)}/?q=${encodeURIComponent(qc.mixed_auto_typed)}">Искать именно: ${qt}</a></p>`);
        }
        if (qc.suggest_latin_layout) {
            const s = this.escapeHtml(qc.suggest_latin_layout);
            parts.push(`<p class="results__correction-line">Возможно, вы имели в виду латиницей: <a href="${this.escapeHtml(base)}/?q=${encodeURIComponent(qc.suggest_latin_layout)}">${s}</a></p>`);
        }
        if (qc.did_you_mean) {
            const s = this.escapeHtml(qc.did_you_mean);
            parts.push(`<p class="results__correction-line">Возможно, вы имели в виду: <a href="${this.escapeHtml(base)}/?q=${encodeURIComponent(qc.did_you_mean)}">${s}</a></p>`);
        }
        el.innerHTML = parts.join('');
        if (parts.length > 0) {
            el.classList.add('results__correction_visible');
        } else {
            el.classList.remove('results__correction_visible');
        }
    }

    renderResults(results) {
        if (!this.elements.cardsContainer) return;

        const startIndex = (this.currentPage - 1) * this.config.resultsPerPage;
        const hq = this.queryForHighlight || this.currentQuery;

        this.elements.cardsContainer.innerHTML = results.map((result, index) => {
            const num = startIndex + index + 1;
            const faviconClass = this.getFaviconClass(num);
            const domain = result.domain || this.extractDomain(result.url);
            return `
            <div class="results__card" data-uid="${this.escapeHtml(result.id || '')}">
                <div class="${faviconClass}">
                    ${num}.
                </div>
                <h3 class="results__card-title">
                    <a href="${this.escapeHtml(result.url)}" target="_blank" rel="noopener">${this.highlightQuery(result.title, hq)}</a>
                </h3>
                <div class="results__card-domain">
                    <img class="results__card-domain-favicon" src="https://${this.escapeHtml(domain)}/favicon.ico" width="18" height="18" alt="" loading="lazy" onerror="this.style.display='none'">${this.escapeHtml(domain)}
                </div>
                <div class="results__card-snippet">
                    ${this.highlightQuery(result.snippet, hq)}
                </div>
                <span class="icon_menu results__card-menu-button" data-card-menu-trigger="${result.id}"></span>
            </div>
        `}).join('');
    }

    /**
     * Render places block (компании)
     */
    renderPlaces(places) {
        if (!this.elements.placesContainer) return;
        if (!places || places.length === 0) {
            this.hidePlaces();
            return;
        }

        this.elements.placesContainer.classList.remove('_hidden');
        this.elements.placesContainer.innerHTML = `
            <div class="places">
                <div class="places__header">
                    <h2 class="places__title">${this.config.messages.placesTitle}</h2>
                </div>
                <div class="places__scroll-wrapper">
                    <button class="places__scroll-btn places__scroll-btn_prev" aria-label="Назад" disabled>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                    <div class="places__list">
                        ${places.map(place => this.renderPlaceCard(place)).join('')}
                    </div>
                    <button class="places__scroll-btn places__scroll-btn_next" aria-label="Вперёд">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </div>
            </div>
        `;

        this.attachPlacesEventListeners();
    }

    /**
     * Render single place card
     */
    renderPlaceCard(place) {
        const hasImages = place.images && place.images.length > 0;
        const isOpen = this.isPlaceOpenNow(place.workingHours);
        const todayHours = this.getHoursLabel(place.workingHours, isOpen);

        return `
            <article class="places__card" data-place-id="${this.escapeHtml(place.id)}">
                <a href="${this.escapeHtml(place.website)}" class="places__card-link" target="_blank" rel="noopener">
                    <div class="places__card-gallery${hasImages ? '' : ' places__card-gallery_empty'}">
                        ${hasImages ? this.renderPlaceGallery(place.images) : this.renderPlaceEmptyGallery(place.category)}
                        ${place.logo ? `
                            <div class="places__card-logo">
                                <img src="${this.escapeHtml(place.logo)}" alt="" loading="lazy" onload="this.parentElement.classList.add('_loaded')" onerror="this.parentElement.classList.add('_hidden')">
                            </div>
                        ` : ''}
                    </div>
                    <div class="places__card-content">
                        <h3 class="places__card-name"><span>${this.escapeHtml(place.name)}</span>${place.verified ? '<svg class="places__card-verified" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Подтверждено"><path d="M12 2l2.4 1.6 2.9-.35 1.05 2.73 2.52 1.5-.35 2.9L22 12l-1.49 1.62.35 2.9-2.52 1.5-1.05 2.73-2.9-.35L12 22l-2.4-1.6-2.9.35-1.05-2.73-2.52-1.5.35-2.9L2 12l1.49-1.62-.35-2.9 2.52-1.5 1.05-2.73 2.9.35z" fill="#1877F2"/><path d="M8 12l2.5 2.5 5.5-5.5" stroke="#fff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>' : ''}</h3>
                        <div class="places__card-meta">
                            ${place.rating ? `
                                <span class="places__card-rating">
                                    <span class="places__card-rating-value ${parseFloat(place.rating) < 2 ? 'places__card-rating-value_red' : parseFloat(place.rating) < 4 ? 'places__card-rating-value_yellow' : ''}">${place.rating}</span>
                                    ${place.reviewsCount ? `<span class="places__card-reviews${(place.category || '').length >= 10 ? ' places__card-reviews_hide-word' : ''}">${this.formatReviewsNum(place.reviewsCount)}<span class="places__card-reviews-word"> ${this.formatReviewsWord(place.reviewsCount)}</span></span>` : ''}
                                </span>
                                <span class="places__card-sep">·</span>
                            ` : ''}
                            <span class="places__card-category">${place.category ? this.escapeHtml(place.category) : ''}</span>
                        </div>
                        <div class="places__card-hours places__card-hours_${isOpen ? 'open' : 'closed'}">
                            ${isOpen ? 'Открыто' : 'Закрыто'}${todayHours ? ` · ${todayHours}` : ''}
                        </div>
                        <div class="places__card-address">${this.escapeHtml(place.address)}</div>
                    </div>
                </a>
                ${place.phone ? `
                    <button class="places__card-phone" data-phone="${this.escapeHtml(place.phone)}">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        Показать телефон
                    </button>
                ` : ''}
            </article>
        `;
    }

    /**
     * Render place gallery with indicators
     */
    renderPlaceGallery(images) {
        const maxImages = Math.min(images.length, 5);
        const visibleImages = images.slice(0, maxImages);

        return `
            <div class="places__card-images" data-images-count="${visibleImages.length}">
                ${visibleImages.map((img, i) => `
                    <img src="${this.escapeHtml(img)}" 
                         alt="" 
                         class="places__card-image${i === 0 ? ' _active' : ''}" 
                         data-index="${i}"
                         ${i === 0 ? '' : 'loading="lazy" '}onerror="window._searchPagination && window._searchPagination.onPlaceImageError(this)">
                `).join('')}
            </div>
            ${visibleImages.length > 1 ? `
                <div class="places__card-indicators">
                    ${visibleImages.map((_, i) => `
                        <span class="places__card-indicator${i === 0 ? ' _active' : ''}" data-index="${i}"></span>
                    `).join('')}
                </div>
            ` : ''}
        `;
    }

    /**
     * Render empty gallery placeholder
     */
    getCategoryIcon(category) {
        const cat = (category || '').toLowerCase();

        const map = [
            // Еда и напитки
            { keys: ['кафе','кофе','coffee'], color: '#7B4F2E', bg: '#F5EDE5',
                icon: `<path d="M17 8h1a4 4 0 010 8h-1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M3 8h14v9a4 4 0 01-4 4H7a4 4 0 01-4-4z" stroke="currentColor" stroke-width="1.5"/><line x1="6" y1="2" x2="6" y2="4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><line x1="10" y1="2" x2="10" y2="4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><line x1="14" y1="2" x2="14" y2="4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>` },
            { keys: ['ресторан','restaurant'], color: '#8B3A3A', bg: '#FAEAEA',
                icon: `<path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 002-2V2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M7 2v20" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M21 15V2a5 5 0 00-5 5v6c0 1.1.9 2 2 2h3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M21 15v7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>` },
            { keys: ['пицц','пиццерия'], color: '#C0562A', bg: '#FDF0E8',
                icon: `<path d="M12 3C7 3 3 7 3 12l9 9 9-9c0-5-4-9-9-9z" stroke="currentColor" stroke-width="1.5"/><circle cx="12" cy="12" r="2" fill="currentColor"/><circle cx="8" cy="9" r="1" fill="currentColor"/><circle cx="15" cy="10" r="1" fill="currentColor"/>` },
            { keys: ['бар','паб','бирн'], color: '#5A6E3A', bg: '#EEF3E5',
                icon: `<path d="M17 11h1a3 3 0 010 6h-1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M9 12v5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M13 12v5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M14 7.5c-1 0-1.44.5-3 .5s-2-.5-3-.5-1.44.5-3 .5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M3 9a1 1 0 011-1h12a1 1 0 011 1v9a1 1 0 01-1 1H4a1 1 0 01-1-1z" stroke="currentColor" stroke-width="1.5"/>` },
            { keys: ['пекарн','булочн','хлеб'], color: '#A07840', bg: '#F7F0E5',
                icon: `<path d="M4 15c0-4.4 3.6-8 8-8s8 3.6 8 8" stroke="currentColor" stroke-width="1.5"/><rect x="2" y="15" width="20" height="4" rx="1" stroke="currentColor" stroke-width="1.5"/>` },
            // Здоровье
            { keys: ['аптек','pharmacy','drug'], color: '#2E7D5E', bg: '#E5F4EE',
                icon: `<path d="M12 2v20M2 12h20" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>` },
            { keys: ['больниц','клиник','медиц','hospital'], color: '#1E6B8A', bg: '#E5F2F8',
                icon: `<rect x="3" y="6" width="18" height="15" rx="1" stroke="currentColor" stroke-width="1.5"/><path d="M3 10h18M8 6V4a1 1 0 011-1h6a1 1 0 011 1v2" stroke="currentColor" stroke-width="1.5"/><path d="M12 13v4M10 15h4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>` },
            { keys: ['стомат','зубн','dental'], color: '#3A6B7A', bg: '#E8F2F5',
                icon: `<path d="M12 2C9 2 6 4.5 6 7c0 1.5.5 2.5 1 4l1 7c.3 1.5 1 2 2 2s1.5-.8 2-2c.5 1.2 1 2 2 2s1.7-.5 2-2l1-7c.5-1.5 1-2.5 1-4 0-2.5-3-5-6-5z" stroke="currentColor" stroke-width="1.5"/>` },
            { keys: ['оптик','линз','очк'], color: '#4A5E8A', bg: '#EAEEf6',
                icon: `<circle cx="7" cy="12" r="4" stroke="currentColor" stroke-width="1.5"/><circle cx="17" cy="12" r="4" stroke="currentColor" stroke-width="1.5"/><path d="M11 12h2M3 12H2M21 12h1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>` },
            // Красота
            { keys: ['салон','парикм','стрижк','барбер'], color: '#5A3A5A', bg: '#F0E8F5',
                icon: `<circle cx="6" cy="6" r="3" stroke="currentColor" stroke-width="1.5"/><circle cx="6" cy="18" r="3" stroke="currentColor" stroke-width="1.5"/><line x1="20" y1="4" x2="8.12" y2="15.88" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><line x1="14.47" y1="14.48" x2="20" y2="20" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><line x1="8.12" y1="8.12" x2="12" y2="12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>` },
            { keys: ['маникюр','ногт','педикюр'], color: '#A0366A', bg: '#FAEBF2',
                icon: `<path d="M8 21V7a4 4 0 018 0v14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M5 21h14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><circle cx="12" cy="7" r="2" fill="currentColor"/>` },
            { keys: ['спа','spa','массаж'], color: '#5A7A5A', bg: '#EAF2EA',
                icon: `<path d="M11 20A7 7 0 019.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>` },
            // Авто
            { keys: ['автосерв','автомойк','шиномонт','автозапч'], color: '#3A5A7A', bg: '#E5EEF5',
                icon: `<path d="M5 17H3v-4l2-6h14l2 6v4h-2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><circle cx="7.5" cy="17.5" r="2.5" stroke="currentColor" stroke-width="1.5"/><circle cx="16.5" cy="17.5" r="2.5" stroke="currentColor" stroke-width="1.5"/>` },
            { keys: ['автосалон','автодилер','автомобил'], color: '#1A3A5A', bg: '#E0EAF5',
                icon: `<path d="M7 17H5a2 2 0 01-2-2v-3l2.5-6h13L21 12v3a2 2 0 01-2 2h-2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><circle cx="8.5" cy="17.5" r="2.5" stroke="currentColor" stroke-width="1.5"/><circle cx="15.5" cy="17.5" r="2.5" stroke="currentColor" stroke-width="1.5"/><path d="M9 11h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>` },
            { keys: ['заправ','азс','бензин'], color: '#4A6A3A', bg: '#EBF3E5',
                icon: `<path d="M3 22V6a2 2 0 012-2h8a2 2 0 012 2v16M3 22h12M7 10h4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M15 8l4 2v8h-1a1 1 0 01-1-1v-3h-2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>` },
            // Магазины
            { keys: ['продукт','супермарк','магазин','гастроном'], color: '#3A6A4A', bg: '#E5F0EA',
                icon: `<path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><path d="M3 6h18M16 10a4 4 0 01-8 0" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>` },
            { keys: ['цветоч','цветы','флорист'], color: '#A03060', bg: '#FAE8F0',
                icon: `<circle cx="12" cy="12" r="2.5" fill="currentColor"/><ellipse cx="12" cy="6" rx="2.2" ry="3.5" stroke="currentColor" stroke-width="1.5"/><ellipse cx="12" cy="18" rx="2.2" ry="3.5" stroke="currentColor" stroke-width="1.5"/><ellipse cx="6" cy="12" rx="3.5" ry="2.2" stroke="currentColor" stroke-width="1.5"/><ellipse cx="18" cy="12" rx="3.5" ry="2.2" stroke="currentColor" stroke-width="1.5"/>` },
            { keys: ['одежд','fashion','бутик','одеяни'], color: '#6A4A7A', bg: '#F0EAF5',
                icon: `<path d="M20.38 3.46L16 2a4 4 0 01-8 0L3.62 3.46a2 2 0 00-1.34 2.23l.58 3.57a1 1 0 00.99.86H6v10c0 1.1.9 2 2 2h8a2 2 0 002-2V10h2.15a1 1 0 00.99-.86l.58-3.57a2 2 0 00-1.34-2.23z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>` },
            { keys: ['книг','книжн','литерат'], color: '#4A3A2A', bg: '#EDE8E0',
                icon: `<path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>` },
            { keys: ['электрон','техник','бытов'], color: '#2A4A6A', bg: '#E2EAF4',
                icon: `<rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M8 21h8M12 17v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>` },
            // Услуги
            { keys: ['банк','финанс','кредит'], color: '#1A5A3A', bg: '#E0F0E8',
                icon: `<line x1="3" y1="22" x2="21" y2="22" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><line x1="6" y1="18" x2="6" y2="11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><line x1="10" y1="18" x2="10" y2="11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><line x1="14" y1="18" x2="14" y2="11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><line x1="18" y1="18" x2="18" y2="11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M12 2L20 7H4L12 2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>` },
            { keys: ['юрид','нотар','адвок','право'], color: '#4A4A6A', bg: '#EAEAF4',
                icon: `<path d="M12 22s-8-3-8-10V4l8-2 8 2v8c0 7-8 10-8 10z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>` },
            { keys: ['почт','доставк','курьер'], color: '#5A5A3A', bg: '#EEEEDF',
                icon: `<path d="M22 8.01V16a2 2 0 01-2 2H4a2 2 0 01-2-2V8.01" stroke="currentColor" stroke-width="1.5"/><path d="M22 8L12 13 2 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" stroke-width="1.5"/>` },
            { keys: ['фитнес','трен','спортзал','зал','gym'], color: '#2A4A2A', bg: '#E2EEE2',
                icon: `<line x1="8" y1="12" x2="16" y2="12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><rect x="4" y="8" width="4" height="8" rx="2" stroke="currentColor" stroke-width="1.5"/><rect x="16" y="8" width="4" height="8" rx="2" stroke="currentColor" stroke-width="1.5"/><rect x="1" y="10" width="4" height="4" rx="1" stroke="currentColor" stroke-width="1.5"/><rect x="19" y="10" width="4" height="4" rx="1" stroke="currentColor" stroke-width="1.5"/>` },
            { keys: ['гостиниц','отель','хостел','hotel'], color: '#4A3A6A', bg: '#EBE8F4',
                icon: `<path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke="currentColor" stroke-width="1.5"/><rect x="9" y="14" width="6" height="8" rx="1" stroke="currentColor" stroke-width="1.5"/>` },
            { keys: ['детск','игрушк','ребенок','ясл'], color: '#7A5A2A', bg: '#F4EFE2',
                icon: `<path d="M12 2a5 5 0 100 10A5 5 0 0012 2z" stroke="currentColor" stroke-width="1.5"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>` },
            { keys: ['образован','школ','курс','учеб'], color: '#2A5A6A', bg: '#E2EEF2',
                icon: `<path d="M22 10v6M2 10l10-5 10 5-10 5-10-5z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M6 12v5c3 3 9 3 12 0v-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>` },
        ];

        for (const entry of map) {
            if (entry.keys.some(k => cat.includes(k))) {
                return { color: entry.color, bg: entry.bg, icon: entry.icon };
            }
        }

        // Дефолт
        return {
            color: '#5A6A7A',
            bg: '#EBEEf2',
            icon: `<path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" stroke="currentColor" stroke-width="1.5"/><circle cx="12" cy="9" r="2.5" stroke="currentColor" stroke-width="1.5"/>`
        };
    }

    renderPlaceEmptyGallery(category) {
        const { color, bg, icon } = this.getCategoryIcon(category);
        return `
            <div class="places__card-images places__card-images_empty" style="background:${bg}">
                <div class="places__card-no-photo" style="color:${color}">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        ${icon}
                    </svg>
                </div>
            </div>
        `;
    }

    /**
     * Hide company card
     */
    hideCompany() {
        if (this.elements.companyContainer) {
            this.elements.companyContainer.innerHTML = '';
            this.elements.companyContainer.classList.add('_hidden');
        }
    }

    /**
     * Render company card
     */
    renderCompany(company) {
        if (!this.elements.companyContainer) return;

        const isOpen = this.isPlaceOpenNow(company.workingHours);
        const todayHours = this.getHoursLabel(company.workingHours, isOpen);
        const { weekly, exceptions } = this.parsePlaceWorkingHours(company.workingHours);

        const dayNames = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
        const todayIndex = (new Date().getDay() + 6) % 7;

        const scheduleRows = weekly.map((hours, i) => {
            const isToday = i === todayIndex;
            const label = hours === '24h' ? 'круглосуточно' : hours === 'closed' ? 'выходной' : hours;
            return `<div class="company-card__hours-row${isToday ? ' company-card__hours-row_today' : ''}">
                <span class="company-card__hours-day">${dayNames[i]}</span>
                <span>${this.escapeHtml(label)}</span>
            </div>`;
        }).join('');

        const images = (company.images || []);
        const hasImages = images.length > 0;

        const arrowSvgPrev = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>`;
        const arrowSvgNext = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>`;

        const galleryHtml = hasImages ? `
            <div class="company-card__gallery-images" data-images-count="${images.length}">
                ${images.map((src, i) => `
                    <img class="company-card__gallery-image${i === 0 ? ' _active' : ''}"
                         src="${this.escapeHtml(src)}"
                         loading="lazy"
                         onerror="this.classList.add('_error')"
                         alt="">
                `).join('')}
                ${images.length > 1 ? `
                <button class="company-card__gallery-arrow company-card__gallery-arrow_prev" data-gallery-prev>${arrowSvgPrev}</button>
                <button class="company-card__gallery-arrow company-card__gallery-arrow_next" data-gallery-next>${arrowSvgNext}</button>
                <div class="company-card__gallery-indicators">
                    ${images.map((_, i) => `<span class="company-card__gallery-indicator${i === 0 ? ' _active' : ''}" data-index="${i}"></span>`).join('')}
                </div>` : ''}
            </div>
        ` : `
            <div class="company-card__gallery-empty">
                ${company.logo ? `<img src="${this.escapeHtml(company.logo)}" alt="" class="company-card__gallery-logo-only">` : ''}
            </div>
        `;

        const phones = company.phones || (company.phone ? [company.phone] : []);
        const phonesHtml = phones.map((p, i) => `
            <a href="tel:${p.replace(/\D/g, '')}" class="company-card__phone${i > 0 ? ' company-card__phone_extra' : ''}">${this.escapeHtml(p)}</a>
        `).join('');
        const extraCount = phones.length - 1;
        const extraWord = extraCount === 1 ? 'номер' : (extraCount < 5 ? 'номера' : 'номеров');
        const morePhonesHtml = phones.length > 1 ? `
            <button class="company-card__phones-more" data-phones-toggle>
                Ещё ${extraCount} ${extraWord}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
        ` : '';

        const menuHtml = (company.menu && company.menu.length > 0) ? `
            <div class="company-card__section">
                <div class="company-card__menu">
                    <div class="company-card__menu-items">
                        ${company.menu.map(item => `
                            <div class="company-card__menu-item">
                                ${item.image ? `<img class="company-card__menu-item-img" src="${this.escapeHtml(item.image)}" alt="" loading="lazy">` : '<div class="company-card__menu-item-img company-card__menu-item-img_empty"></div>'}
                                <div class="company-card__menu-item-info">
                                    <div class="company-card__menu-item-name">${this.escapeHtml(item.name)}</div>
                                    ${item.price ? `<div class="company-card__menu-item-price">${this.escapeHtml(item.price)}</div>` : ''}
                                </div>
                            </div>
                        `).join('')}
                    </div>
                    <a href="${this.escapeHtml(company.website || '#')}" target="_blank" rel="noopener" class="company-card__menu-all">Посмотреть всё</a>
                </div>
            </div>
        ` : '';

        this.elements.companyContainer.innerHTML = `
            <div class="company-card">
                <div class="company-card__gallery${!hasImages ? ' company-card__gallery_empty' : ''}">
                    ${galleryHtml}
                    ${company.logo ? `<div class="company-card__logo"><img src="${this.escapeHtml(company.logo)}" alt=""></div>` : ''}
                </div>
                <div class="company-card__body">
                    <div class="company-card__name">
                        ${this.escapeHtml(company.name)}
                        ${company.verified ? '<svg class="company-card__verified" viewBox="0 0 24 24" fill="none"><path d="M12 2l2.4 1.6 2.9-.35 1.05 2.73 2.52 1.5-.35 2.9L22 12l-1.49 1.62.35 2.9-2.52 1.5-1.05 2.73-2.9-.35L12 22l-2.4-1.6-2.9.35-1.05-2.73-2.52-1.5.35-2.9L2 12l1.49-1.62-.35-2.9 2.52-1.5 1.05-2.73 2.9.35z" fill="#1877F2"/><path d="M8 12l2.5 2.5 5.5-5.5" stroke="#fff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>' : ''}
                    </div>
                    <div class="company-card__category-row">
                        ${company.category ? `<span class="company-card__category">${this.escapeHtml(company.category)}</span>` : '<span></span>'}
                        <a href="/edit-company/${company.id}" class="company-card__owner-link-inline">Вы владелец?</a>
                    </div>

                    <div class="company-card__actions">
                        ${phones[0] ? `<a href="tel:${phones[0].replace(/\D/g,'')}" class="company-card__action-btn company-card__action-btn_call"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.68A2 2 0 012 1h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>Позвонить</a>` : ''}
                        ${company.website ? `<a href="${this.escapeHtml(company.website)}" target="_blank" rel="noopener" class="company-card__action-btn"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>Сайт</a>` : ''}
                        ${company.coordinates ? `<a href="https://maps.yandex.ru/?pt=${company.coordinates.split(',').reverse().join(',')}&z=16" target="_blank" rel="noopener" class="company-card__action-btn"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>Маршрут</a>` : ''}
                        <button class="company-card__action-btn" data-share-company><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>Поделиться</button>
                    </div>

                    <div class="company-card__sections-grid">
                    ${company.workingHours ? `
                    <div class="company-card__section">
                        <div class="company-card__hours">
                            <svg class="company-card__hours-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                            <div class="company-card__hours-info">
                                <div class="company-card__hours-status${isOpen ? '' : ' company-card__hours-status_closed'}">${isOpen ? 'Открыто' : 'Закрыто'}${todayHours ? ` · ${todayHours}` : ''}</div>
                                <button class="company-card__hours-toggle" data-hours-toggle>
                                    Расписание
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="6 9 12 15 18 9"/></svg>
                                </button>
                                <div class="company-card__hours-schedule" data-hours-schedule>${scheduleRows}</div>
                            </div>
                        </div>
                    </div>` : ''}

                    ${company.address ? `
                    <div class="company-card__section">
                        <div class="company-card__address">
                            <svg class="company-card__address-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                            <div>
                                <div class="company-card__address-text">${this.escapeHtml(company.address)}</div>
                                ${company.coordinates ? `<a href="https://maps.yandex.ru/?pt=${company.coordinates.split(',').reverse().join(',')}&z=16" target="_blank" rel="noopener" class="company-card__address-link">Показать на карте</a>` : ''}
                            </div>
                        </div>
                    </div>` : ''}

                    ${phones.length > 0 ? `
                    <div class="company-card__section">
                        <div class="company-card__phones">
                            <svg class="company-card__phones-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.68A2 2 0 012 1h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>
                            <div class="company-card__phones-list">
                                ${phonesHtml}
                                ${morePhonesHtml}
                            </div>
                        </div>
                    </div>` : ''}

                    ${company.website ? `
                    <div class="company-card__section">
                        <div class="company-card__website">
                            <svg class="company-card__website-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
                            <a href="${this.escapeHtml(company.website)}" target="_blank" rel="noopener">${this.escapeHtml(company.website.replace(/^https?:\/\//, ''))}</a>
                        </div>
                    </div>` : ''}
                    </div><!-- /.sections-grid -->

                    ${menuHtml}
                </div>

            </div>
        `;

        this.elements.companyContainer.classList.remove('_hidden');
        this.attachCompanyEventListeners();
    }

    /**
     * Attach company card event listeners
     */
    attachCompanyEventListeners() {
        const container = this.elements.companyContainer;
        if (!container) return;

        // Расписание toggle
        const hoursToggle = container.querySelector('[data-hours-toggle]');
        const hoursSchedule = container.querySelector('[data-hours-schedule]');
        if (hoursToggle && hoursSchedule) {
            hoursToggle.addEventListener('click', () => {
                const open = hoursSchedule.classList.toggle('_open');
                hoursToggle.classList.toggle('_open', open);
            });
        }

        // Доп. телефоны toggle
        const phonesToggle = container.querySelector('[data-phones-toggle]');
        if (phonesToggle) {
            phonesToggle.addEventListener('click', () => {
                container.querySelectorAll('.company-card__phone_extra').forEach(el => {
                    el.style.display = 'block';
                });
                phonesToggle.style.display = 'none';
            });
        }

        // Галерея — навигация
        const galleryImgs = () => [...container.querySelectorAll('.company-card__gallery-image')].filter(el => !el.classList.contains('_error'));
        const galleryInds = () => container.querySelectorAll('.company-card__gallery-indicator');
        const prevBtn = container.querySelector('[data-gallery-prev]');
        const nextBtn = container.querySelector('[data-gallery-next]');

        const getActiveIdx = () => {
            const imgs = galleryImgs();
            return imgs.findIndex(el => el.classList.contains('_active'));
        };

        const goToSlide = (idx) => {
            const imgs = galleryImgs();
            const inds = galleryInds();
            if (!imgs.length) return;
            const newIdx = (idx + imgs.length) % imgs.length;
            imgs.forEach((img, i) => img.classList.toggle('_active', i === newIdx));
            inds.forEach((el, i) => el.classList.toggle('_active', i === newIdx));

        };

        if (prevBtn) prevBtn.addEventListener('click', () => goToSlide(getActiveIdx() - 1));
        if (nextBtn) nextBtn.addEventListener('click', () => goToSlide(getActiveIdx() + 1));

        // Клик по индикаторам
        const indicators = container.querySelectorAll('.company-card__gallery-indicator');
        indicators.forEach(ind => {
            ind.addEventListener('click', () => goToSlide(parseInt(ind.dataset.index)));
        });

        // Свайп и тап по зонам (мобильный)
        const galleryEl = container.querySelector('.company-card__gallery-images');
        if (galleryEl) {
            let touchStartX = 0;
            let touchDeltaX = 0;
            galleryEl.addEventListener('touchstart', e => {
                touchStartX = e.touches[0].clientX;
                touchDeltaX = 0;
            }, { passive: true });
            galleryEl.addEventListener('touchmove', e => {
                touchDeltaX = e.touches[0].clientX - touchStartX;
            }, { passive: true });
            galleryEl.addEventListener('touchend', e => {
                if (Math.abs(touchDeltaX) > 40) {
                    // свайп
                    goToSlide(getActiveIdx() + (touchDeltaX < 0 ? 1 : -1));
                } else if (Math.abs(touchDeltaX) < 8) {
                    // тап по зоне
                    const rect = galleryEl.getBoundingClientRect();
                    const tapX = e.changedTouches[0].clientX - rect.left;
                    goToSlide(getActiveIdx() + (tapX < rect.width / 2 ? -1 : 1));
                }
            });
        }

        // Кнопка «Поделиться»
        const shareBtn = container.querySelector('[data-share-company]');
        if (shareBtn) {
            shareBtn.addEventListener('click', () => {
                if (navigator.share) {
                    navigator.share({ title: document.title, url: location.href });
                } else {
                    navigator.clipboard?.writeText(location.href);
                }
            });
        }
    }

    /**
     * Hide places block
     */
    hidePlaces() {
        if (this.elements.placesContainer) {
            this.elements.placesContainer.innerHTML = '';
            this.elements.placesContainer.classList.add('_hidden');
        }
    }

    /**
     * Attach event listeners for places
     */
    attachPlacesEventListeners() {
        if (!this.elements.placesContainer) return;

        const list = this.elements.placesContainer.querySelector('.places__list');
        const prevBtn = this.elements.placesContainer.querySelector('.places__scroll-btn_prev');
        const nextBtn = this.elements.placesContainer.querySelector('.places__scroll-btn_next');

        // Scroll buttons
        if (prevBtn && nextBtn && list) {
            prevBtn.addEventListener('click', () => { list.classList.remove('_no-snap'); this.scrollPlaces(list, -1); });
            nextBtn.addEventListener('click', () => { list.classList.remove('_no-snap'); this.scrollPlaces(list, 1); });

            list.addEventListener('scroll', () => this.updatePlacesScrollButtons(list, prevBtn, nextBtn));
            this.updatePlacesScrollButtons(list, prevBtn, nextBtn);
        }

        // Hover slideshow for images (с задержкой 300мс)
        const galleries = this.elements.placesContainer.querySelectorAll('.places__card-images[data-images-count]');
        galleries.forEach(gallery => {
            const count = parseInt(gallery.dataset.imagesCount);
            if (count > 1) {
                let hoverTimer = null;
                let hoverActive = false;
                let lastMouseEvent = null;

                const galleryEl = gallery.closest('.places__card-gallery');

                gallery.addEventListener('mouseenter', (e) => {
                    lastMouseEvent = e;
                    hoverTimer = setTimeout(() => {
                        hoverActive = true;
                        if (galleryEl) galleryEl.classList.add('_gallery-hover');
                        if (lastMouseEvent) this.onPlaceGalleryHover(lastMouseEvent, gallery, parseInt(gallery.dataset.imagesCount));
                    }, 300);
                });

                gallery.addEventListener('mousemove', (e) => {
                    lastMouseEvent = e;
                    if (hoverActive) this.onPlaceGalleryHover(e, gallery, parseInt(gallery.dataset.imagesCount));
                });

                gallery.addEventListener('mouseleave', () => {
                    clearTimeout(hoverTimer);
                    hoverActive = false;
                    if (galleryEl) galleryEl.classList.remove('_gallery-hover');
                    this.onPlaceGalleryLeave(gallery);
                });
            }
        });

        // Пауза анимаций при наведении на карусель
        const scrollWrapper = this.elements.placesContainer.querySelector('.places__scroll-wrapper');
        if (list && scrollWrapper) {
            list.addEventListener('mouseenter', () => scrollWrapper.classList.add('_anim-paused'));
            list.addEventListener('mouseleave', () => scrollWrapper.classList.remove('_anim-paused'));
        }

        // Wheel scroll on desktop — по одной карточке за прокрутку
        if (list) {
            list.addEventListener('wheel', (e) => {
                if (e.deltaY === 0) return;
                e.preventDefault();
                list.classList.remove('_no-snap');
                this.scrollPlaces(list, e.deltaY > 0 ? 1 : -1);
            }, { passive: false });
        }

        // Mouse drag scroll with momentum on desktop
        if (list) {
            let isDown = false;
            let startX = 0;
            let scrollStart = 0;
            let velX = 0;
            let rafId = null;
            let hasDragged = false;

            const cancelMomentum = () => {
                if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
            };

            const momentumLoop = () => {
                velX *= 0.85;
                list.scrollLeft += velX;
                if (Math.abs(velX) > 0.5) {
                    rafId = requestAnimationFrame(momentumLoop);
                } else {
                    list.classList.remove('_dragging');
                }
            };

            list.addEventListener('mousedown', (e) => {
                isDown = true;
                hasDragged = false;
                startX = e.pageX;
                scrollStart = list.scrollLeft;
                velX = 0;
                cancelMomentum();
                list.classList.add('_dragging');
                list.classList.add('_no-snap');
                e.preventDefault();
            });

            const onMouseUp = () => {
                if (!isDown) return;
                isDown = false;
                if (Math.abs(velX) > 0.5) {
                    rafId = requestAnimationFrame(momentumLoop);
                } else {
                    list.classList.remove('_dragging');
                }
            };

            document.addEventListener('mouseup', onMouseUp);

            list.addEventListener('mousemove', (e) => {
                if (!isDown) return;
                e.preventDefault();
                const dx = e.pageX - startX;
                if (Math.abs(dx) > 5) hasDragged = true;
                const prevScrollLeft = list.scrollLeft;
                list.scrollLeft = scrollStart - dx;
                velX = list.scrollLeft - prevScrollLeft;
            });

            list.addEventListener('mouseup', onMouseUp);
            list.addEventListener('mouseleave', onMouseUp);

            // Блокируем клик по карточке если был drag
            list.addEventListener('click', (e) => {
                if (hasDragged) {
                    e.preventDefault();
                    e.stopPropagation();
                    hasDragged = false;
                }
            }, true);
        }

        // Phone reveal
        const phoneButtons = this.elements.placesContainer.querySelectorAll('.places__card-phone');
        phoneButtons.forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const phone = btn.dataset.phone;
                btn.innerHTML = `<a href="tel:${phone}">${phone}</a>`;
                btn.classList.add('_revealed');
            });
        });
    }

    /**
     * Gallery hover - switch images
     */
    onPlaceGalleryHover(e, gallery, count) {
        if (gallery.closest('.places__list._dragging')) return;
        const rect = gallery.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const width = rect.width;
        const index = Math.floor((x / width) * count);

        this.showPlaceImage(gallery, Math.min(index, count - 1));
    }

    /**
     * Gallery mouse leave - return to first image
     */
    onPlaceImageError(img) {
        img.classList.add('_error');
        this.log('Place image failed to load:', img.src);
        const gallery = img.closest('.places__card-images');
        if (!gallery) return;

        // Пересчитываем рабочие картинки
        const allImgs = Array.from(gallery.querySelectorAll('.places__card-image'));
        const working = allImgs.filter(i => !i.classList.contains('_error'));
        const count = working.length;

        // Обновляем data-images-count
        gallery.dataset.imagesCount = count;

        // Переназначаем data-index
        working.forEach((i, idx) => i.dataset.index = idx);

        // Обновляем индикаторы
        const card = gallery.closest('.places__card');
        const indicators = card ? card.querySelectorAll('.places__card-indicator') : [];
        const indicatorsWrapper = card ? card.querySelector('.places__card-indicators') : null;

        if (count <= 1 && indicatorsWrapper) {
            indicatorsWrapper.style.display = 'none';
        } else {
            const workingIndicators = Array.from(indicators).filter((_, idx) => idx < count);
            workingIndicators.forEach((ind, idx) => ind.dataset.index = idx);
            Array.from(indicators).filter((_, idx) => idx >= count).forEach(ind => ind.style.display = 'none');
        }

        // Если активная картинка упала — активируем первую рабочую
        if (img.classList.contains('_active') && working.length > 0) {
            this.showPlaceImage(gallery, 0);
        }
    }

    onPlaceGalleryLeave(gallery) {
        this.showPlaceImage(gallery, 0);
    }

    /**
     * Show specific image in gallery
     */
    showPlaceImage(gallery, index) {
        const images = Array.from(gallery.querySelectorAll('.places__card-image')).filter(i => !i.classList.contains('_error'));
        const card = gallery.closest('.places__card');
        const indicators = card ? Array.from(card.querySelectorAll('.places__card-indicator')).filter(i => i.style.display !== 'none') : [];

        images.forEach((img, i) => {
            img.classList.toggle('_active', i === index);
        });

        indicators.forEach((ind, i) => {
            ind.classList.toggle('_active', i === index);
        });
    }

    /**
     * Scroll places list
     */
    scrollPlaces(list, direction) {
        const cardWidth = list.querySelector('.places__card')?.offsetWidth || 220;
        const gap = 16;
        const scrollAmount = cardWidth + gap;

        list.scrollBy({
            left: direction * scrollAmount,
            behavior: 'smooth'
        });
    }

    /**
     * Update scroll buttons state
     */
    updatePlacesScrollButtons(list, prevBtn, nextBtn) {
        const isAtStart = list.scrollLeft <= 0;
        const isAtEnd = list.scrollLeft + list.clientWidth >= list.scrollWidth - 1;

        prevBtn.disabled = isAtStart;
        nextBtn.disabled = isAtEnd;
    }

    /**
     * Parse workingHours string
     */
    parsePlaceWorkingHours(str) {
        if (!str) return { weekly: [], exceptions: {} };

        const parts = str.split(';');
        const weekly = parts.slice(0, 7);
        const exceptions = {};

        parts.slice(7).forEach(part => {
            const [date, time] = part.split('=');
            if (date && time) {
                exceptions[date] = time;
            }
        });

        return { weekly, exceptions };
    }

    /**
     * Get today's hours for place
     */
    getPlaceTodayHours(workingHours) {
        const { weekly, exceptions } = this.parsePlaceWorkingHours(workingHours);
        const now = new Date();

        // Check exception for today
        const today = now.toISOString().slice(0, 10);
        if (exceptions[today]) {
            return exceptions[today];
        }

        // Get from weekly schedule (0=Mon, 6=Sun)
        const dayIndex = (now.getDay() + 6) % 7;
        return weekly[dayIndex] || null;
    }

    /**
     * Get formatted today hours
     */
    getPlaceTodayHoursFormatted(workingHours) {
        return this.getHoursLabel(workingHours, this.isPlaceOpenNow(workingHours));
    }

    /**
     * Get hours label depending on open/closed state.
     * Open:   "до HH:MM" — closing time of current interval
     * Closed: "до HH:MM" — next opening time (today or next days)
     */
    getHoursLabel(workingHours, isOpen) {
        const now = new Date();
        const currentTime = now.toTimeString().slice(0, 5);

        if (isOpen) {
            const hours = this.getPlaceTodayHours(workingHours);
            if (hours === '24h') return 'круглосуточно';
            const intervals = hours.split(',');
            for (const interval of intervals) {
                const [open, close] = interval.split('-');
                if (currentTime >= open && currentTime < close) {
                    return `до ${close}`;
                }
            }
            return '';
        }

        // Closed: check only the next calendar day
        const { weekly, exceptions } = this.parsePlaceWorkingHours(workingHours);

        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowDate = tomorrow.toISOString().slice(0, 10);
        const tomorrowIndex = (tomorrow.getDay() + 6) % 7; // 0=Mon…6=Sun

        const tomorrowHours = exceptions[tomorrowDate] !== undefined
            ? exceptions[tomorrowDate]
            : (weekly[tomorrowIndex] || null);

        if (!tomorrowHours || tomorrowHours === 'closed') return '';
        if (tomorrowHours === '24h') return 'до 00:00';

        const [firstOpen] = tomorrowHours.split(',')[0].split('-');
        return firstOpen ? `до ${firstOpen}` : '';
    }

    /**
     * Check if place is open now
     */
    isPlaceOpenNow(workingHours) {
        const hours = this.getPlaceTodayHours(workingHours);

        if (!hours || hours === 'closed') return false;
        if (hours === '24h') return true;

        const now = new Date();
        const currentTime = now.toTimeString().slice(0, 5);

        // Support breaks (multiple intervals)
        const intervals = hours.split(',');
        return intervals.some(interval => {
            const [open, close] = interval.split('-');
            return currentTime >= open && currentTime < close;
        });
    }

    /**
     * Format reviews count
     */
    formatReviewsNum(count) {
        return count >= 1000 ? `${Math.floor(count / 1000)}K` : `${count}`;
    }

    formatReviewsWord(count) {
        const lastDigit = count % 10;
        const lastTwoDigits = count % 100;
        if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return 'отзывов';
        if (lastDigit === 1) return 'отзыв';
        if (lastDigit >= 2 && lastDigit <= 4) return 'отзыва';
        return 'отзывов';
    }

    /**
     * Render pagination
     */
    renderPagination(currentPage, totalPages) {
        // Обновляем link rel="prev/next/canonical" в head
        this.updateHeadLinks(currentPage, totalPages);

        if (!this.elements.paginationContainer || totalPages <= 1) {
            if (this.elements.paginationContainer) {
                this.elements.paginationContainer.innerHTML = '';
            }
            return;
        }

        const maxLinks = this.config.maxPaginationLinks;
        let startPage = Math.max(1, currentPage - Math.floor(maxLinks / 2));
        let endPage = Math.min(totalPages, startPage + maxLinks - 1);

        // Adjust start if we're near the end
        if (endPage - startPage < maxLinks - 1) {
            startPage = Math.max(1, endPage - maxLinks + 1);
        }

        let paginationHtml = '<nav class="pagination" role="navigation" aria-label="Pagination">';
        paginationHtml += '<ul class="pagination-list" style="list-style:none;display:flex;gap:8px;padding:0;margin:0;align-items:center;">';

        // Previous button
        if (currentPage > 1) {
            paginationHtml += `
                <li>
                    <a class="pagination__page-prev" href="${this.buildPageUrl(currentPage - 1)}" rel="prev" aria-label="Предыдущая">
                        Предыдущая
                    </a>
                </li>
            `;
        }

        // First page
        if (startPage > 1) {
            paginationHtml += `
                <li>
                    <a class="pagination__page-link" href="${this.buildPageUrl(1)}" aria-label="На страницу 1">1</a>
                </li>
            `;
            if (startPage > 2) {
                paginationHtml += '<li><span class="pagination__ellipsis">...</span></li>';
            }
        }

        // Page numbers
        for (let i = startPage; i <= endPage; i++) {
            if (i === currentPage) {
                paginationHtml += `
                    <li>
                        <span class="pagination__page-current" aria-current="page">${i}</span>
                    </li>
                `;
            } else {
                paginationHtml += `
                    <li>
                        <a class="pagination__page-link" href="${this.buildPageUrl(i)}" aria-label="На страницу ${i}">${i}</a>
                    </li>
                `;
            }
        }

        // Многоточие в конце (без ссылки на последнюю страницу)
        if (endPage < totalPages) {
            paginationHtml += '<li><span class="pagination__ellipsis">...</span></li>';
        }

        // Next button
        if (currentPage < totalPages) {
            paginationHtml += `
                <li>
                    <a class="pagination__page-next" href="${this.buildPageUrl(currentPage + 1)}" rel="next" aria-label="Следующая">
                        Следующая
                    </a>
                </li>
            `;
        }

        paginationHtml += '</ul></nav>';

        this.elements.paginationContainer.innerHTML = paginationHtml;
    }

    /**
     * Построить URL для страницы пагинации
     */
    buildPageUrl(page) {
        const params = new URLSearchParams();
        params.set('q', this.currentQuery);
        if (page > 1) {
            params.set('page', page);
        }
        return `${this.config.baseUrl}/?${params.toString()}`;
    }

    /**
     * Обновить link rel="prev/next/canonical" в head
     */
    updateHeadLinks(currentPage, totalPages) {
        const baseUrl = this.config.baseUrl;

        // Удаляем старые link теги
        document.querySelectorAll('link[rel="prev"], link[rel="next"], link[rel="canonical"]').forEach(el => el.remove());

        // canonical — всегда указывает на текущую страницу
        const canonical = document.createElement('link');
        canonical.rel = 'canonical';
        canonical.href = this.currentQuery ? this.buildPageUrl(currentPage) : baseUrl + '/';
        document.head.appendChild(canonical);

        // prev — если не первая страница
        if (currentPage > 1) {
            const prev = document.createElement('link');
            prev.rel = 'prev';
            prev.href = this.buildPageUrl(currentPage - 1);
            document.head.appendChild(prev);
        }

        // next — если не последняя страница
        if (currentPage < totalPages) {
            const next = document.createElement('link');
            next.rel = 'next';
            next.href = this.buildPageUrl(currentPage + 1);
            document.head.appendChild(next);
        }
    }

    /**
     * Render footer with test mode notice
     */
    renderFooter(centered = false) {
        // Удаляем старый футер если есть
        const existingFooter = document.querySelector('.results__footer');
        if (existingFooter) {
            existingFooter.remove();
        }

        const footer = document.createElement('div');
        footer.className = 'results__footer' + (centered ? ' results__footer_centered' : '');
        footer.innerHTML = `
            Региональный поисковик работает в тестовом режиме. 
            <a href="https://t.me/teltsn" target="_blank" rel="noopener nofollow">Связаться с нами</a>
        `;

        // Вставляем после кнопки load-more (или после пагинации/карточек если её нет)
        const loadMore = document.querySelector('.results__load-more');
        if (loadMore) {
            loadMore.after(footer);
        } else if (this.elements.paginationContainer && this.elements.paginationContainer.innerHTML) {
            this.elements.paginationContainer.after(footer);
        } else if (this.elements.cardsContainer) {
            this.elements.cardsContainer.after(footer);
        }
    }

    /**
     * Highlight search query in text
     */
    highlightQuery(text, query) {
        if (!text || !query) return this.escapeHtml(text || '');

        const escapedText = this.escapeHtml(text);
        const words = query.split(/\s+/).filter(w => w.length > 0);

        let result = escapedText;
        words.forEach(word => {
            const regex = new RegExp(`(${this.escapeRegex(word)})`, 'gi');
            result = result.replace(regex, '<b>$1</b>');
        });

        return result;
    }

    /**
     * Extract domain from URL
     */
    extractDomain(url) {
        try {
            const urlObj = new URL(url);
            return urlObj.hostname.replace(/^www\./, '');
        } catch {
            return url;
        }
    }

    /**
     * Получить класс для favicon в зависимости от числа
     */
    getFaviconClass(num) {
        let cls = 'results__card-favicon';
        if (num >= 1000) {
            cls += ' results__card-favicon_4col';
        } else if (num >= 100) {
            cls += ' results__card-favicon_3col';
        }
        return cls;
    }

    /**
     * Показать состояние загрузки со скелетонами (с задержкой)
     */
    showLoading() {
        // Скрываем кнопку "Загрузить ещё" сразу
        this.hideLoadMoreButton();

        // Показываем скелетоны с задержкой, чтобы не мигать при быстром ответе
        this.skeletonTimer = setTimeout(() => {
            if (this.elements.cardsContainer && this.isLoading) {
                this.elements.cardsContainer.classList.add('results__cards_loading');

                const skeletonCount = this.config.resultsPerPage || 10;
                const skeletons = Array(skeletonCount).fill(0).map(() => `
                    <div class="results__skeleton">
                        <div class="results__skeleton-favicon"></div>
                        <div class="results__skeleton-title"></div>
                        <div class="results__skeleton-domain"></div>
                        <div class="results__skeleton-snippet"></div>
                    </div>
                `).join('');

                this.elements.cardsContainer.innerHTML = skeletons;
            }
        }, this.config.skeletonDelay);
    }

    /**
     * Скрыть состояние загрузки
     */
    hideLoading() {
        // Отменяем таймер скелетонов если ответ пришёл раньше
        if (this.skeletonTimer) {
            clearTimeout(this.skeletonTimer);
            this.skeletonTimer = null;
        }

        if (this.elements.cardsContainer) {
            this.elements.cardsContainer.classList.remove('results__cards_loading');
        }
    }

    /**
     * Показать сообщение "ничего не найдено" с популярными запросами
     */
    showNoResults() {
        if (this.elements.cardsContainer) {
            const title = this.config.messages.noResultsTitle.replace('{query}', this.escapeHtml(this.currentQuery));

            this.elements.cardsContainer.innerHTML = `
                <div class="results__empty">
                    <p>${title}</p>
                    <p>${this.config.messages.noResultsHint}</p>
                </div>
                <div class="results__populars">
                    <p class="results__populars-title">${this.config.messages.popularQueriesTitle}</p>
                    <ul class="results__populars-list"></ul>
                </div>
            `;

            // Загружаем популярные запросы
            this.loadPopulars();
        }
        if (this.elements.paginationContainer) {
            this.elements.paginationContainer.innerHTML = '';
        }
        this.hidePlaces();
        this.hideLoadMoreButton();
        this.renderFooter(true);
    }

    /**
     * Загрузка и рендер популярных запросов
     */
    async loadPopulars() {
        try {
            const response = await this.fetchWithRetry(this.config.apiPopulars);
            const data = await response.json();

            if (Array.isArray(data) && data.length > 0) {
                this.renderPopulars(data.slice(0, 10));
            }
        } catch (err) {
            this.error('Ошибка загрузки популярных запросов:', err);
            // Скрываем блок при ошибке
            const popularsSection = this.elements.cardsContainer?.querySelector('.results__populars');
            if (popularsSection) {
                popularsSection.classList.add('_hidden');
            }
        }
    }

    /**
     * Render popular queries list
     */
    renderPopulars(tags) {
        const listElement = this.elements.cardsContainer?.querySelector('.results__populars-list');
        if (!listElement) return;

        listElement.innerHTML = tags
            .map(tag => `<li>${this.escapeHtml(tag)}</li>`)
            .join('');

        // Handle tag click
        listElement.querySelectorAll('li').forEach(li => {
            li.addEventListener('click', () => {
                const query = li.textContent || li.innerText;
                if (this.elements.searchInput) {
                    this.elements.searchInput.value = query;

                    // Пересчитать высоту textarea
                    if (window.searcherHandle?.resizeAndToggleClear) {
                        window.searcherHandle.resizeAndToggleClear();
                    }

                    // Update clear button
                    const clearButton = document.querySelector('[data-searcher-clear]');
                    if (clearButton) {
                        clearButton.classList.add('_active');
                    }
                }
                // Trigger search
                this.currentQuery = query;
                this.currentPage = 1;
                this.loadResults(query, 1, true);
            });
        });
    }

    /**
     * Обновить видимость кнопки "Загрузить ещё"
     */
    updateLoadMoreButton(currentPage, totalPages) {
        const loadMoreBtn = document.querySelector('.results__more-button, .results__load-more button, [data-load-more-button]');
        const loadMoreContainer = document.querySelector('.results__load-more');

        if (loadMoreBtn || loadMoreContainer) {
            const element = loadMoreContainer || loadMoreBtn;
            if (currentPage >= totalPages) {
                element.classList.add('_hidden');
            } else {
                element.classList.remove('_hidden');
            }
        }
    }

    /**
     * Скрыть кнопку "Загрузить ещё"
     */
    hideLoadMoreButton() {
        const loadMoreBtn = document.querySelector('.results__more-button, .results__load-more button, [data-load-more-button]');
        const loadMoreContainer = document.querySelector('.results__load-more');

        if (loadMoreContainer) {
            loadMoreContainer.classList.add('_hidden');
        } else if (loadMoreBtn) {
            loadMoreBtn.classList.add('_hidden');
        }
    }

    /**
     * Определение мобильного устройства
     */
    detectMobile() {
        return /Mobi|Android|iPhone/i.test(navigator.userAgent)
            || window.innerWidth < this.config.mobileMaxWidth;
    }

    /**
     * Инициализация автозагрузки при скролле
     */
    initAutoLoad() {
        if (!('IntersectionObserver' in window)) {
            this.warn('IntersectionObserver не поддерживается, автозагрузка отключена');
            return;
        }

        this.loadMoreObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting && !this.isLoading) {
                        this.onAutoLoad();
                    }
                });
            },
            { threshold: this.config.autoLoadThreshold }
        );

        // Наблюдаем за контейнером кнопки
        const loadMoreContainer = document.querySelector(this.config.loadMoreSelector);
        if (loadMoreContainer) {
            this.loadMoreObserver.observe(loadMoreContainer);
            this.log('Автозагрузка включена');
        }
    }

    /**
     * Обработчик автозагрузки
     */
    onAutoLoad() {
        // Проверяем есть ли ещё страницы
        if (this.currentPage >= this.totalPages) {
            return;
        }

        // Проверяем есть ли запрос
        if (!this.currentQuery) {
            return;
        }

        this.log('Автозагрузка страницы', this.currentPage + 1);

        // Загружаем следующую страницу
        this.appendResults(this.currentQuery, this.currentPage + 1);
    }

    /* ==================== КНОПКА "ВВЕРХ" ==================== */

    /**
     * Создание кнопки "Вверх"
     */
    createScrollTopButton() {
        // Проверяем, не создана ли уже
        if (document.querySelector('.results__2top-button')) return;

        const button = document.createElement('button');
        button.className = 'results__2top-button';
        button.setAttribute('aria-label', 'Вернуться наверх');
        button.innerHTML = `
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 15l-6-6-6 6"/>
            </svg>
        `;

        button.addEventListener('click', () => this.scrollToTop());

        document.body.appendChild(button);
        this.scrollTopButton = button;

        // Слушатель скролла для показа/скрытия
        this.boundHandlers.scroll = this.onScrollForTopButton.bind(this);
        window.addEventListener('scroll', this.boundHandlers.scroll, { passive: true });

        this.log('Кнопка "Вверх" создана');
    }

    /**
     * Показать кнопку "Вверх"
     */
    showScrollTopButton() {
        if (this.scrollTopButton) {
            this.scrollTopButton.classList.add('_visible');
        }
    }

    /**
     * Скрыть кнопку "Вверх"
     */
    hideScrollTopButton() {
        if (this.scrollTopButton) {
            this.scrollTopButton.classList.remove('_visible');
        }
    }

    /**
     * Обработчик скролла для кнопки "Вверх"
     */
    onScrollForTopButton() {
        // Скрываем если близко к верху (меньше 500px от верха)
        if (window.scrollY < 500) {
            this.hideScrollTopButton();
        } else if (this.currentPage > 1) {
            // Показываем только если была подгрузка
            this.showScrollTopButton();
        }
    }

    /**
     * Плавная прокрутка вверх
     */
    scrollToTop() {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }

    /**
     * Показать сообщение об ошибке
     */
    showError() {
        if (this.elements.cardsContainer) {
            this.elements.cardsContainer.innerHTML = `
                <div class="results__error">
                    <p>${this.config.messages.errorTitle}</p>
                    <p>${this.config.messages.errorHint}</p>
                </div>
            `;
        }
        if (this.elements.paginationContainer) {
            this.elements.paginationContainer.innerHTML = '';
        }
        this.hidePlaces();
        this.hideLoadMoreButton();
        this.renderFooter(true);
    }

    /**
     * Escape HTML
     */
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    /**
     * Экранирование спецсимволов regex
     */
    escapeRegex(text) {
        return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    /**
     * Логирование (только если debug: true)
     */
    log(...args) {
        if (this.config.debug) {
            console.log('[SearchPagination]', ...args);
        }
    }

    warn(...args) {
        if (this.config.debug) {
            console.warn('[SearchPagination]', ...args);
        }
    }

    error(...args) {
        if (this.config.debug) {
            console.error('[SearchPagination]', ...args);
        }
    }

    /**
     * Fetch с повторными попытками при ошибке
     */
    async fetchWithRetry(url, options = {}) {
        let lastError;

        for (let attempt = 1; attempt <= this.config.retryAttempts; attempt++) {
            try {
                const response = await fetch(url, options);

                if (!response.ok) {
                    throw new Error(`HTTP ошибка: ${response.status}`);
                }

                return response;
            } catch (err) {
                lastError = err;
                this.warn(`Попытка ${attempt}/${this.config.retryAttempts} не удалась:`, err.message);

                // Не ждём после последней попытки
                if (attempt < this.config.retryAttempts) {
                    await new Promise(resolve => setTimeout(resolve, this.config.retryDelay));
                }
            }
        }

        throw lastError;
    }

    /**
     * Очистка
     */
    destroy() {
        // Отменяем таймер скелетонов
        if (this.skeletonTimer) {
            clearTimeout(this.skeletonTimer);
            this.skeletonTimer = null;
        }

        // Отключаем IntersectionObserver автозагрузки
        if (this.loadMoreObserver) {
            this.loadMoreObserver.disconnect();
            this.loadMoreObserver = null;
        }

        // Удаляем кнопку "Вверх" и scroll listener
        if (this.boundHandlers.scroll) {
            window.removeEventListener('scroll', this.boundHandlers.scroll);
        }
        if (this.scrollTopButton) {
            this.scrollTopButton.remove();
            this.scrollTopButton = null;
        }

        window.removeEventListener('popstate', this.boundHandlers.popstate);

        if (this.elements.paginationContainer) {
            this.elements.paginationContainer.removeEventListener('click', this.boundHandlers.paginationClick);
        }

        if (this.elements.searchButton) {
            this.elements.searchButton.removeEventListener('click', this.boundHandlers.searchSubmit);
        }

        // Cleanup "Load More" button listener
        if (this.boundHandlers.loadMore) {
            const loadMoreBtn = document.querySelector('.results__more-button');
            if (loadMoreBtn) {
                loadMoreBtn.removeEventListener('click', this.boundHandlers.loadMore);
            }
        }
    }
}
export default SearchPagination;